import { ArrowLeft, BookOpen, Headphones, HelpCircle, Menu, Moon, Sun } from 'lucide-react';
import { useEffect, useRef, useState, type FC, type SVGProps } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip.js';
import { useHomePopup } from '../../features/common/home-popup-context.js';
import { useAuth } from '../../lib/auth-context.js';
import { isCustomerAdmin } from '../../lib/roles.js';
import { useTheme } from '../../lib/theme-context.js';
import { BrandMark } from '../BrandMark.js';
import FusionIcon from '../icons/FusionIcon.js';
import PlanningIcon from '../icons/PlanningIcon.js';
import WorkfrontIcon from '../icons/WorkfrontIcon.js';

const COLLAPSE_STORAGE_KEY = 'wf-coe-spa-sidebar-collapsed';

function readCollapsed(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(COLLAPSE_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function persistCollapsed(value: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(COLLAPSE_STORAGE_KEY, value ? '1' : '0');
  } catch {
    /* localStorage unavailable (private mode, quota) — fall back to
     *  per-tab state. */
  }
}

/**
 * Left-rail navigation. Mirrors the legacy
 * `wf-audit/src/slices/common/components/sidebar/{Provider,Item}.tsx`
 * markup — `bg-gradient-to-b from-gradientStart to-gradientEnd`
 * shell, yellow brand mark + "Center of Excellence" header, and
 * per-item pills that go from `bg-bgSidebarItem` with a blue-outline
 * border to `bg-gradientEnd` with a yellow-outline border when active.
 *
 * Per-product nav sets (Fusion / Workfront / Planning) come from a
 * single route table; the active section is inferred from the
 * current URL.
 */

interface NavLeaf {
  kind: 'leaf';
  to: string;
  label: string;
  icon: JSX.Element;
  /** Hidden from customer viewers (V-02); the route itself is gated too. */
  adminOnly?: boolean;
}

interface NavGroup {
  kind: 'group';
  /** URL prefix that activates the group (matches the legacy
   *  `isActive: pathname?.startsWith('/scenario')` pattern). */
  activePrefix: string;
  label: string;
  icon: JSX.Element;
  children: ReadonlyArray<NavLeaf>;
}

export type NavEntry = NavLeaf | NavGroup;

const FUSION_NAV: ReadonlyArray<NavEntry> = [
  { kind: 'leaf', to: '/home', label: 'Home', icon: <HomeGlyph /> },
  {
    kind: 'group',
    activePrefix: '/scenarios',
    label: 'Scenarios',
    icon: <BoltGlyph />,
    children: [
      { kind: 'leaf', to: '/scenarios', label: 'Business Executive', icon: <BriefcaseGlyph /> },
      {
        kind: 'leaf',
        to: '/scenarios/administrator',
        label: 'Workfront Administrator',
        icon: <CogGlyph />,
      },
      {
        kind: 'leaf',
        to: '/scenarios/developer',
        label: 'Fusion Developer',
        icon: <BoltGlyph />,
      },
    ],
  },
  { kind: 'leaf', to: '/dashboard', label: 'Daily Dashboard', icon: <DashboardGlyph /> },
  { kind: 'leaf', to: '/presentation', label: 'Presentation', icon: <DeckGlyph /> },
  {
    kind: 'group',
    activePrefix: '/value',
    label: 'Value',
    icon: <PieGlyph />,
    children: [
      { kind: 'leaf', to: '/value', label: 'Value Metrics', icon: <CogGlyph /> },
      { kind: 'leaf', to: '/value/scenario-kpis', label: 'Scenario KPIs', icon: <BoltGlyph /> },
    ],
  },
  {
    kind: 'group',
    activePrefix: '/kpi',
    label: 'KPI Builder',
    icon: <SlidersGlyph />,
    children: [
      { kind: 'leaf', to: '/kpi/types', label: 'KPI Type', icon: <CogGlyph /> },
      { kind: 'leaf', to: '/kpi', label: 'KPI Metric', icon: <BoltGlyph /> },
    ],
  },
];

export const WORKFRONT_NAV: ReadonlyArray<NavEntry> = [
  { kind: 'leaf', to: '/home', label: 'Home', icon: <HomeGlyph /> },
  { kind: 'leaf', to: '/workfront', label: 'WF Objects in Fusion', icon: <FolderGlyph /> },
  {
    kind: 'leaf',
    to: '/workfront/custom-forms-migration',
    label: 'Custom Forms Migration',
    icon: <WorkflowGlyph />,
    adminOnly: true,
  },
];

export const PLANNING_NAV: ReadonlyArray<NavEntry> = [
  { kind: 'leaf', to: '/home', label: 'Home', icon: <HomeGlyph /> },
  { kind: 'leaf', to: '/planning/er-diagram', label: 'ER Diagram', icon: <CombineGlyph /> },
  {
    kind: 'leaf',
    to: '/planning/backup',
    label: 'Backup',
    icon: <HardDriveGlyph />,
    adminOnly: true,
  },
  {
    kind: 'leaf',
    to: '/planning/restore',
    label: 'Restore',
    icon: <UploadGlyph />,
    adminOnly: true,
  },
];

/**
 * Bottom utility group. Mirrors legacy `wf-audit/.../sidebar/Provider.tsx`
 * which only renders **User Settings** + **Support** at the bottom —
 * the new SPA had an unused `Setup` placeholder that operators found
 * confusing (its icon clashed with the theme-toggle Sun glyph). Removed.
 *
 * **Support** isn't a `NavLeaf` because legacy renders it as a dropdown
 * of two **external** links (Documentation + Get Support), not as an
 * internal route. See `SupportExternalDropdown` below.
 */
const SUPPORT_NAV: ReadonlyArray<NavLeaf> = [
  { kind: 'leaf', to: '/user', label: 'User Settings', icon: <UserGlyph /> },
];

/**
 * External URLs the Support dropdown points at. Match the legacy
 * `wf-audit/.../sidebar/Provider.tsx` defaults:
 *   - Documentation: `import.meta.env.VITE_DOCS_URL` (legacy used
 *     `process.env.NEXT_PUBLIC_DOCS_URL`); falls back to the FCOE
 *     introduction page on workfocus.io when the env var isn't set
 *     (operator-requested: docs link should point at
 *     `/fcoe/docs/introduction`).
 *   - Get Support: `https://workfocus.io/fcoe/support` (legacy
 *     hard-codes this; no env override).
 */
const DOCS_URL =
  (import.meta as { env?: { VITE_DOCS_URL?: string } }).env?.VITE_DOCS_URL ??
  'https://workfocus.io/fcoe/docs/introduction';
const GET_SUPPORT_URL = 'https://workfocus.io/fcoe/support';

function activeNav(pathname: string): ReadonlyArray<NavEntry> {
  if (pathname.startsWith('/workfront')) return WORKFRONT_NAV;
  if (pathname.startsWith('/planning')) return PLANNING_NAV;
  return FUSION_NAV;
}

export function visibleNav(
  items: ReadonlyArray<NavEntry>,
  isAdmin: boolean,
): ReadonlyArray<NavEntry> {
  return isAdmin ? items : items.filter((i) => i.kind !== 'leaf' || !i.adminOnly);
}

export function NavSidebar(): JSX.Element {
  const location = useLocation();
  const { session } = useAuth();
  const items = visibleNav(activeNav(location.pathname), isCustomerAdmin(session));
  const [collapsed, setCollapsed] = useState<boolean>(() => readCollapsed());
  const asideRef = useRef<HTMLElement>(null);
  const { open: openHomePopup } = useHomePopup();
  useEffect(() => {
    persistCollapsed(collapsed);
  }, [collapsed]);

  // Click-outside auto-collapse — when the sidebar is expanded and the
  // operator clicks anywhere outside it (including on a nav link inside
  // it, which fires before navigation), collapse back to the rail.
  // Operator-reported: "when we click outside of left menu - menu
  // should collapse". Skip when already collapsed.
  useEffect(() => {
    if (collapsed) return;
    function onDown(e: MouseEvent): void {
      const target = e.target as Node | null;
      if (asideRef.current && target && !asideRef.current.contains(target)) {
        setCollapsed(true);
      }
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [collapsed]);

  return (
    <aside
      ref={asideRef}
      // Fixed-position overlay so expanding/collapsing the sidebar
      // doesn't reflow the main content. Operator-reported: "layout
      // shifts to the right when left menu is expanded." With this
      // change the sidebar floats on top of the content's left edge
      // when expanded; the content keeps a constant 64px left padding
      // (set in `IframeShell`) matching the collapsed width.
      className={`fixed left-0 top-0 bottom-0 z-30 flex shrink-0 flex-col overflow-y-auto px-2 py-4 text-white shadow-xl transition-[width] duration-200 ${
        collapsed ? 'w-[64px]' : 'w-[260px]'
      }`}
      style={{
        background: 'linear-gradient(180deg, var(--gradient-start) 0%, var(--gradient-end) 100%)',
      }}
    >
      {/* Brand + "Center of Excellence" tagline. Logo-only when
          collapsed (the brand-mark stays the visual anchor).
          Operator-reported follow-up: "when user click on logo - home
          page content should show as a popup without switching a
          page". So the brand-mark is a button (not a Link) that
          raises the home-popup overlay via `useHomePopup`; the
          chromed Home page is still reachable via the sidebar's
          dedicated Home nav item which keeps the `Link to="/"`.

          When the rail is collapsed, wrap with a Radix Tooltip so
          hovering the icon reveals the "Center of Excellence" label
          + the click affordance (operator parity-polish — the
          expanded tagline disappears at 64px width, so a hover label
          replaces it). */}
      {collapsed ? (
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={openHomePopup}
                aria-label="Open WorkFocus Center of Excellence home overlay"
                className="mb-3 flex items-center justify-center px-1 transition-opacity duration-200 hover:opacity-80"
                style={{ color: 'var(--brand-yellow)' }}
              >
                <BrandMark size={36} />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right" sideOffset={8}>
              Center of Excellence — Home
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        <button
          type="button"
          onClick={openHomePopup}
          aria-label="Open WorkFocus Center of Excellence home overlay"
          title="Home overlay"
          className="mb-3 flex items-center gap-3 px-1 transition-opacity duration-200 hover:opacity-80"
          style={{ color: 'var(--brand-yellow)' }}
        >
          <BrandMark size={44} />
          <span className="text-left text-base font-bold leading-[22px]">Center of Excellence</span>
        </button>
      )}

      {/* Collapse / Expand pill — matches the legacy
          `wf-audit/.../sidebar/Provider.tsx` first `SidebarItem` which
          rendered as a full-width row with `move-left` icon + "Collapse"
          label (expanded) or `menu` icon + "Expand" label (collapsed).
          Uses the same blue-on-dark `bg-sidebar-item` / brand-blue
          border treatment as `NavLeafLink` so it visually belongs in
          the rail. Operator-reported: "Collapse/expand button is
          different comparing to legacy system, please fix it". */}
      <CollapseTogglePill collapsed={collapsed} onToggle={() => setCollapsed((c) => !c)} />

      {/* Product picker — port of the legacy `SidebarItemPopup`.
          A single full-width pill that opens a compact dropdown panel
          directly beneath it (same idiom as the Support dropdown
          below). Operator-reported on the previous iteration: "no
          need to expand popover vertically, need to extend button
          itself to cover left menu width" — i.e. the button is the
          full-width control, the dropdown is a contained panel. */}
      {!collapsed ? <ProductPicker /> : null}

      <div className="mb-2.5 mt-0.5 border-b border-white/30" />

      <nav className="flex grow flex-col gap-2">
        {items.map((item) =>
          item.kind === 'leaf' ? (
            <NavLeafLink key={item.to} item={item} collapsed={collapsed} />
          ) : (
            <NavGroupExpando
              key={item.activePrefix}
              group={item}
              pathname={location.pathname}
              collapsed={collapsed}
            />
          ),
        )}

        <div className="grow" />

        {/* Bottom utility group — User Settings + the Theme switcher
            + Support. Matches legacy `wf-audit/.../sidebar/Provider.tsx`
            placement where the `ModeToggle` lived in the sidebar's
            bottom rail. The sidebar stays branded WorkFocus-dark in
            both themes (the gradient is part of the brand identity),
            so the toggle uses `text-white` explicitly here rather
            than the theme-aware `text-foreground` token. */}
        <div className="mb-1 border-t border-white/20 pt-2" />
        <ThemeNavItem collapsed={collapsed} />
        {SUPPORT_NAV.map((item) => (
          <NavLeafLink key={item.to} item={item} collapsed={collapsed} />
        ))}
        <SupportExternalDropdown collapsed={collapsed} />
      </nav>
    </aside>
  );
}

/* ─────────────────────────────────────────────────────────────────
   SupportExternalDropdown — matches legacy
   `wf-audit/.../sidebar/Provider.tsx` Support entry: a
   `help-circle` pill that opens a popup with two **external** links
   (Documentation + Get Support). Replaces the new SPA's previous
   `/support` internal route + placeholder pages — legacy never had
   internal Support pages; the operator went straight to docs /
   the public support form.
   ───────────────────────────────────────────────────────────────── */
function SupportExternalDropdown({ collapsed }: { collapsed: boolean }): JSX.Element {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Click-outside dismiss — match the ProductPicker behaviour.
  useEffect(() => {
    if (!open) return;
    function handler(e: MouseEvent): void {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Collapsed-rail variant — clicking the icon goes straight to the
  // Get-Support form in a new tab. Operator-reported: "if left panel
  // is collapsed - open Get Support page right away". Skips the
  // popup entirely; the operator hasn't expanded the rail so they're
  // not browsing — they need help fast.
  if (collapsed) {
    return (
      <a
        href={GET_SUPPORT_URL}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Get Support"
        title="Get Support"
        className="group relative flex min-h-12 items-center justify-center rounded-lg border transition-all duration-200 hover:shadow-sm"
        style={{
          borderColor: 'var(--brand-blue)',
          background: 'var(--bg-sidebar-item)',
        }}
      >
        <HelpCircle
          style={{ color: 'var(--brand-yellow)' }}
          className="h-[18px] w-[18px] shrink-0"
        />
      </a>
    );
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-label="Support"
        className="group relative flex min-h-12 w-full items-center gap-2.5 rounded-lg border px-3 transition-all duration-200 hover:shadow-sm"
        style={{
          borderColor: 'var(--brand-blue)',
          background: 'var(--bg-sidebar-item)',
        }}
      >
        <HelpCircle
          style={{ color: 'var(--brand-yellow)' }}
          className="h-[18px] w-[18px] shrink-0"
        />
        <span className="flex-1 text-left text-white">Support</span>
        <span
          aria-hidden
          className={`text-white/60 transition-transform ${open ? 'rotate-180' : ''}`}
        >
          ▾
        </span>
      </button>
      {open ? (
        <div
          className="absolute bottom-full left-0 right-0 z-50 mb-1 flex flex-col gap-1 rounded-lg border p-1.5"
          style={{
            borderColor: 'var(--brand-blue)',
            background:
              'linear-gradient(180deg, var(--gradient-end) 0%, var(--gradient-start) 100%)',
            boxShadow: '0 12px 28px rgba(0,0,0,0.5)',
          }}
        >
          <ExternalSupportLink
            href={DOCS_URL}
            label="Documentation"
            icon={BookOpen}
            onClick={() => setOpen(false)}
          />
          <ExternalSupportLink
            href={GET_SUPPORT_URL}
            label="Get Support"
            icon={Headphones}
            onClick={() => setOpen(false)}
          />
        </div>
      ) : null}
    </div>
  );
}

function ExternalSupportLink({
  href,
  label,
  icon: Icon,
  onClick,
}: {
  href: string;
  label: string;
  icon: typeof BookOpen;
  onClick: () => void;
}): JSX.Element {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onClick}
      className="flex items-center gap-2 rounded-md px-2.5 py-2 text-sm text-white/85 transition-colors hover:bg-white/10 hover:text-white"
    >
      <Icon style={{ color: 'var(--brand-yellow)' }} className="h-4 w-4 shrink-0" />
      <span>{label}</span>
    </a>
  );
}

/* ─────────────────────────────────────────────────────────────────
   CollapseTogglePill — full-width nav-pill that flips the sidebar's
   collapsed state. Mirrors the legacy `wf-audit/.../sidebar/Provider.tsx`
   first `SidebarItem` (icon: `move-left` when expanded, `menu` when
   collapsed; label: "Collapse" / "Expand"). Same blue-on-dark pill
   treatment as `NavLeafLink` so it visually belongs in the rail.
   ───────────────────────────────────────────────────────────────── */
function CollapseTogglePill({
  collapsed,
  onToggle,
}: {
  collapsed: boolean;
  onToggle: () => void;
}): JSX.Element {
  const Icon = collapsed ? Menu : ArrowLeft;
  const label = collapsed ? 'Expand' : 'Collapse';
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={`${label} sidebar`}
      title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      className={[
        'group relative mb-2 flex min-h-12 items-center rounded-lg border transition-all duration-200 hover:shadow-sm',
        collapsed ? 'justify-center px-0' : 'gap-2.5 px-3',
      ].join(' ')}
      style={{
        borderColor: 'var(--brand-blue)',
        background: 'var(--bg-sidebar-item)',
      }}
    >
      <Icon style={{ color: 'var(--brand-yellow)' }} className="h-[18px] w-[18px] shrink-0" />
      {!collapsed ? <span className="text-white">{label}</span> : null}
    </button>
  );
}

/* ─────────────────────────────────────────────────────────────────
   ThemeNavItem — single-click Dark ↔ Light toggle styled as a sidebar
   pill. Mirrors the layout of `NavLeafLink` (brand-blue border, dark
   sidebar-item background) so it lives naturally in the bottom rail.
   Collapsed: shows the active mode's icon. Click flips to the other.
   Expanded: shows icon + label ("Dark mode" / "Light mode") +
     a hint of the destination state. Click anywhere on the pill flips.
   The whole pill is the click target — no embedded segment buttons.
   ───────────────────────────────────────────────────────────────── */
function ThemeNavItem({ collapsed }: { collapsed: boolean }): JSX.Element {
  const { resolvedTheme, toggleTheme } = useTheme();
  const Active = resolvedTheme === 'dark' ? Moon : Sun;
  // Operator-requested copy change: "Dark mode → Dark Theme" /
  // "Light mode → Light Theme". The button's behaviour is unchanged
  // — single click flips between the two.
  const label = resolvedTheme === 'dark' ? 'Dark Theme' : 'Light Theme';
  const ariaLabel = `Theme: ${resolvedTheme}. Click to switch to ${
    resolvedTheme === 'dark' ? 'light' : 'dark'
  } theme.`;

  return (
    <button
      type="button"
      onClick={toggleTheme}
      aria-label={ariaLabel}
      title={collapsed ? ariaLabel : undefined}
      className={[
        'group relative flex min-h-12 items-center rounded-lg border transition-all duration-200 hover:shadow-sm',
        collapsed ? 'justify-center px-0' : 'gap-2.5 px-3',
      ].join(' ')}
      style={{
        borderColor: 'var(--brand-blue)',
        background: 'var(--bg-sidebar-item)',
      }}
    >
      <Active style={{ color: 'var(--brand-yellow)' }} className="h-[18px] w-[18px] shrink-0" />
      {!collapsed ? <span className="text-white">{label}</span> : null}
    </button>
  );
}

/* ─────────────────────────────────────────────────────────────────
   NavLeafLink — single nav row. Port of `SidebarItem`: blue-border
   pill on `bg-sidebar-item`, switching to yellow-border on
   `gradient-end` when the route is active.
   ───────────────────────────────────────────────────────────────── */
function NavLeafLink({ item, collapsed }: { item: NavLeaf; collapsed: boolean }): JSX.Element {
  return (
    <NavLink
      to={item.to}
      // Always exact-match leaves. Without `end`, `NavLink` treats
      // `/workfront` (WF Objects in Fusion) as a parent of
      // `/workfront/custom-forms-migration` and lights up the wrong
      // pill. Group-level activation (e.g. `/scenarios/*`) is handled
      // separately by `NavGroupExpando`'s `startsWith` check.
      end
      title={collapsed ? item.label : undefined}
      className={({ isActive }) =>
        [
          'group relative flex min-h-12 items-center gap-2.5 rounded-lg border transition-all duration-200',
          collapsed ? 'justify-center px-0' : 'px-3',
          isActive ? 'shadow-md' : 'hover:shadow-sm',
        ].join(' ')
      }
      style={({ isActive }) =>
        ({
          borderColor: isActive ? 'var(--brand-yellow)' : 'var(--brand-blue)',
          background: isActive ? 'var(--gradient-end)' : 'var(--bg-sidebar-item)',
        }) as React.CSSProperties
      }
    >
      <span style={{ color: 'var(--brand-yellow)' }} className="shrink-0">
        {item.icon}
      </span>
      {!collapsed ? <span className="text-white">{item.label}</span> : null}
    </NavLink>
  );
}

/* ─────────────────────────────────────────────────────────────────
   NavGroupExpando — collapsible parent. Port of `SidebarItemDropdown`
   from `wf-audit`. Header pill behaves like a `NavLeafLink` but
   toggles a list of indented `NavLeafLink` children below. Auto-
   opens when the URL is under the group's `activePrefix`.
   ───────────────────────────────────────────────────────────────── */
function NavGroupExpando({
  group,
  pathname,
  collapsed,
}: {
  group: NavGroup;
  pathname: string;
  collapsed: boolean;
}): JSX.Element {
  const groupActive = pathname.startsWith(group.activePrefix);
  const [open, setOpen] = useState(groupActive);

  // Auto-open when navigating INTO the group, but don't force-close
  // when navigating away (the user may have explicitly opened it).
  useEffect(() => {
    if (groupActive) setOpen(true);
  }, [groupActive]);

  // When collapsed, render only the group icon — clicking jumps to
  // the first leaf (most-recently-active leaf would be nicer but the
  // simpler heuristic is fine here).
  if (collapsed) {
    return (
      <NavLink
        to={group.children[0]?.to ?? group.activePrefix}
        title={group.label}
        className={[
          'group relative flex min-h-12 items-center justify-center rounded-lg border transition-all duration-200',
          groupActive ? 'shadow-md' : 'hover:shadow-sm',
        ].join(' ')}
        style={{
          borderColor: groupActive ? 'var(--brand-yellow)' : 'var(--brand-blue)',
          background: groupActive ? 'var(--gradient-end)' : 'var(--bg-sidebar-item)',
        }}
      >
        <span style={{ color: 'var(--brand-yellow)' }} className="shrink-0">
          {group.icon}
        </span>
      </NavLink>
    );
  }

  return (
    <div className="flex flex-col">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="group relative flex min-h-12 items-center gap-2.5 rounded-lg border px-3 transition-all duration-200 hover:shadow-sm"
        style={{
          borderColor: groupActive ? 'var(--brand-yellow)' : 'var(--brand-blue)',
          background: groupActive ? 'var(--gradient-end)' : 'var(--bg-sidebar-item)',
        }}
      >
        <span style={{ color: 'var(--brand-yellow)' }} className="shrink-0">
          {group.icon}
        </span>
        <span className="flex-1 text-left text-white">{group.label}</span>
        <span
          aria-hidden
          className={`text-white/60 transition-transform ${open ? 'rotate-180' : ''}`}
        >
          ▾
        </span>
      </button>
      {open ? (
        <div className="ml-3 mt-1 flex flex-col gap-1 border-l border-white/20 pl-2">
          {group.children.map((child) => (
            <NavLink
              key={child.to}
              to={child.to}
              end={child.to === group.activePrefix}
              className={({ isActive }) =>
                [
                  'flex min-h-9 items-center gap-2 rounded-md px-2 text-sm transition-colors',
                  isActive ? 'font-semibold' : 'hover:bg-white/5',
                ].join(' ')
              }
              style={({ isActive }) => ({
                color: isActive ? 'var(--brand-yellow)' : '#e5edff',
              })}
            >
              <span style={{ color: 'var(--brand-yellow)' }} className="shrink-0">
                {child.icon}
              </span>
              <span>{child.label}</span>
            </NavLink>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
   ProductPicker — port of `SidebarItemPopup` from
   `wf-audit/src/slices/common/components/sidebar/ItemPopup.tsx`.
   Click toggles a 3-card popup (Fusion / Workfront / Planning); the
   active product is inferred from the current URL.
   ───────────────────────────────────────────────────────────────── */
interface ProductChoice {
  icon: FC<SVGProps<SVGSVGElement>>;
  text: string;
  description: string;
  to: string;
  /** First path segment that marks this product as active. */
  root?: string;
}

const PRODUCTS: ReadonlyArray<ProductChoice> = [
  {
    icon: FusionIcon,
    text: 'Fusion',
    description:
      'Leverage our deep expertise in Adobe Workfront Fusion to help you automate workflows.',
    to: '/scenarios/developer',
  },
  {
    icon: WorkfrontIcon,
    text: 'Workfront',
    description: 'Designed to unlock the full potential on your work management ecosystem.',
    to: '/workfront',
    root: 'workfront',
  },
  {
    icon: PlanningIcon,
    text: 'Planning',
    description:
      "Integrate AEM Assets that let's you easily discover, govern, and activate assets.",
    to: '/planning/er-diagram',
    root: 'planning',
  },
];

function ProductPicker(): JSX.Element {
  const location = useLocation();
  const firstSegment = location.pathname.split('/')[1] ?? '';
  const active = PRODUCTS.find((p) => p.root === firstSegment) ?? PRODUCTS[0]!; // fall back to Fusion
  const ActiveIcon = active.icon;
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Click-outside dismiss — same idiom as SupportExternalDropdown.
  useEffect(() => {
    if (!open) return;
    function handler(e: MouseEvent): void {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Close the dropdown after navigation (clicking a Link inside fires
  // the route change immediately).
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <div ref={containerRef} className="relative mb-3">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-label={`Product picker — current: ${active.text}`}
        className="flex min-h-12 w-full items-center rounded-lg border px-3 transition-all duration-200 hover:shadow-sm"
        style={{
          borderColor: 'var(--brand-blue)',
          background: 'linear-gradient(180deg, var(--gradient-start) 0%, var(--gradient-end) 100%)',
        }}
      >
        <ActiveIcon className="h-6 w-6 shrink-0" />
        <span className="ml-2 flex-1 text-left text-nowrap text-white">{active.text}</span>
        <span
          aria-hidden
          className={`text-white/60 transition-transform ${open ? 'rotate-180' : ''}`}
        >
          ▾
        </span>
      </button>
      {open ? (
        <div
          className="absolute left-0 right-0 top-full z-50 mt-1 flex flex-col gap-1 rounded-lg border p-1.5"
          style={{
            borderColor: 'var(--brand-blue)',
            background:
              'linear-gradient(180deg, var(--gradient-end) 0%, var(--gradient-start) 100%)',
            boxShadow: '0 12px 28px rgba(0,0,0,0.5)',
          }}
        >
          {PRODUCTS.map((p) => {
            const Icon = p.icon;
            const isActive = p.text === active.text;
            return (
              <Link
                key={p.text}
                to={p.to}
                onClick={() => setOpen(false)}
                className={`flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm transition-colors ${
                  isActive ? 'bg-white/10' : 'hover:bg-white/10'
                }`}
              >
                <Icon className="h-5 w-5 shrink-0" />
                <span className="flex-1 text-white">{p.text}</span>
                {isActive ? (
                  <span
                    aria-hidden
                    className="h-1.5 w-1.5 shrink-0 rounded-full"
                    style={{ background: 'var(--brand-yellow)' }}
                  />
                ) : null}
              </Link>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

/* Inline 18×18 line icons — yellow stroke = currentColor inherits
   from the wrapper's `color: var(--brand-yellow)`. */
function BriefcaseGlyph(): JSX.Element {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <rect x="2" y="5" width="14" height="10" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M6 5V3.5C6 2.95 6.45 2.5 7 2.5H11C11.55 2.5 12 2.95 12 3.5V5"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <line x1="2" y1="9" x2="16" y2="9" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}
function HomeGlyph(): JSX.Element {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path
        d="M3 8L9 3L15 8V15H11V11H7V15H3V8Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  );
}
function BoltGlyph(): JSX.Element {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path
        d="M10 1L4 10H8L7 17L14 7H9L10 1Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  );
}
function DashboardGlyph(): JSX.Element {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <rect x="2" y="2" width="6" height="7" stroke="currentColor" strokeWidth="1.5" />
      <rect x="10" y="2" width="6" height="4" stroke="currentColor" strokeWidth="1.5" />
      <rect x="10" y="8" width="6" height="8" stroke="currentColor" strokeWidth="1.5" />
      <rect x="2" y="11" width="6" height="5" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}
function DeckGlyph(): JSX.Element {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <rect x="1" y="3" width="16" height="10" stroke="currentColor" strokeWidth="1.5" />
      <path d="M7 16H11M9 14V16" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}
function PieGlyph(): JSX.Element {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M9 1V9L15 13" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="9" cy="9" r="7" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}
function SlidersGlyph(): JSX.Element {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M3 5H15M3 9H10M3 13H13" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="13" cy="5" r="1.5" fill="currentColor" />
      <circle cx="8" cy="9" r="1.5" fill="currentColor" />
      <circle cx="11" cy="13" r="1.5" fill="currentColor" />
    </svg>
  );
}
function FolderGlyph(): JSX.Element {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path
        d="M2 5H7L9 7H16V15H2V5Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  );
}
function WorkflowGlyph(): JSX.Element {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <rect x="2" y="2" width="5" height="5" stroke="currentColor" strokeWidth="1.5" />
      <rect x="11" y="2" width="5" height="5" stroke="currentColor" strokeWidth="1.5" />
      <rect x="6" y="11" width="6" height="5" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M4.5 7V9.5C4.5 10.5 5.5 11 6.5 11H11.5C12.5 11 13.5 10.5 13.5 9.5V7"
        stroke="currentColor"
        strokeWidth="1.5"
      />
    </svg>
  );
}
function CombineGlyph(): JSX.Element {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <rect x="2" y="2" width="7" height="7" stroke="currentColor" strokeWidth="1.5" />
      <rect x="9" y="9" width="7" height="7" stroke="currentColor" strokeWidth="1.5" />
      <path d="M9 9L9 4M9 9L4 9" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}
function HardDriveGlyph(): JSX.Element {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <rect x="2" y="3" width="14" height="6" stroke="currentColor" strokeWidth="1.5" />
      <rect x="2" y="9" width="14" height="6" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="5" cy="6" r="0.8" fill="currentColor" />
      <circle cx="5" cy="12" r="0.8" fill="currentColor" />
    </svg>
  );
}
function UploadGlyph(): JSX.Element {
  // Up-arrow into a tray — mirrors lucide-react's `Upload` used on the
  // PlanningBackupPage's "Trigger restore" CTA so the sidebar +
  // page-header iconography line up.
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M9 12V2M9 2L5 6M9 2L13 6" stroke="currentColor" strokeWidth="1.5" />
      <path d="M3 12V14a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V12" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}
function CogGlyph(): JSX.Element {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <circle cx="9" cy="9" r="2.5" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M9 2V4M9 14V16M16 9H14M4 9H2M14 4L12.5 5.5M5.5 12.5L4 14M14 14L12.5 12.5M5.5 5.5L4 4"
        stroke="currentColor"
        strokeWidth="1.5"
      />
    </svg>
  );
}
function UserGlyph(): JSX.Element {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <circle cx="9" cy="6" r="3" stroke="currentColor" strokeWidth="1.5" />
      <path d="M2 16C2 13 5 11 9 11C13 11 16 13 16 16" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}
function HelpGlyph(): JSX.Element {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <circle cx="9" cy="9" r="7" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M7 7C7 6 8 5 9 5C10 5 11 6 11 7C11 8.5 9 8 9 10"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <circle cx="9" cy="13" r="0.7" fill="currentColor" />
    </svg>
  );
}
